import { ColorSet, RENDER_CONSTANTS } from "../core/types";

/**
 * Path cache entry
 */
interface PathSet {
  frontFace: Path2D | null;
  topFace: Path2D;
  rightFace: Path2D;
  outline: Path2D;
}

/**
 * Factory for creating and caching place sprites
 *
 * Pattern: Flyweight Factory Pattern
 */
export class PlaceSpriteFactory {
  private pathCache = new Map<string, PathSet>();
  private offscreenCache = new Map<string, OffscreenCanvas>();
  public stats = { hits: 0, misses: 0 };

  /**
   * Get or create sprite
   */
  getSprite(
    w: number,
    h: number,
    dx: number,
    dy: number,
    colorSet: ColorSet,
    stroke: string,
    lw: number
  ): OffscreenCanvas {
    const key = `${w.toFixed(1)}_${h.toFixed(1)}_${dx.toFixed(1)}_${dy.toFixed(
      1
    )}_${colorSet.top}_${stroke}_${lw.toFixed(1)}`;

    let offscreen = this.offscreenCache.get(key);

    if (!offscreen) {
      this.stats.misses++;
      offscreen = this.createSprite(w, h, dx, dy, colorSet, stroke, lw);
      this.offscreenCache.set(key, offscreen);
    } else {
      this.stats.hits++;
    }

    return offscreen;
  }

  /**
   * Create a new sprite
   */
  private createSprite(
    w: number,
    h: number,
    dx: number,
    dy: number,
    colorSet: ColorSet,
    stroke: string,
    lw: number
  ): OffscreenCanvas {
    if (w <= 0 || h <= 0) {
      return new OffscreenCanvas(1, 1);
    }

    const minX = Math.min(0, dx);
    const minY = Math.min(0, dy);
    const maxX = w + Math.max(0, dx);
    const maxY = h + Math.max(0, dy);
    const pad = Math.ceil(Math.abs(lw) * 2);

    const offscreen = new OffscreenCanvas(
      Math.ceil(maxX - minX + pad),
      Math.ceil(maxY - minY + pad)
    );

    const ctx = offscreen.getContext("2d") as OffscreenCanvasRenderingContext2D;
    ctx.translate(-minX + pad / 2, -minY + pad / 2);

    const paths = this.getPaths(w, h, dx, dy);

    // Draw faces
    ctx.fillStyle = colorSet.face;
    ctx.fillRect(0, 0, w, h);

    ctx.fillStyle = colorSet.top;
    ctx.fill(paths.topFace);

    ctx.fillStyle = colorSet.right;
    ctx.fill(paths.rightFace);

    // Draw outline
    ctx.strokeStyle = stroke;
    ctx.lineWidth = lw;
    ctx.stroke(paths.outline);

    return offscreen;
  }

  /**
   * Get or create Path2D objects
   */
  private getPaths(w: number, h: number, dx: number, dy: number): PathSet {
    const key = `${w.toFixed(2)}_${h.toFixed(2)}_${dx.toFixed(2)}_${dy.toFixed(2)}`;
    let paths = this.pathCache.get(key);

    if (!paths) {
      const topFace = new Path2D();
      topFace.moveTo(0, 0);
      topFace.lineTo(dx, dy);
      topFace.lineTo(w + dx, dy);
      topFace.lineTo(w, 0);
      topFace.closePath();

      const rightFace = new Path2D();
      rightFace.moveTo(w, 0);
      rightFace.lineTo(w + dx, dy);
      rightFace.lineTo(w + dx, h + dy);
      rightFace.lineTo(w, h);
      rightFace.closePath();

      const outline = new Path2D();
      outline.moveTo(0, 0);
      outline.lineTo(0, h);
      outline.lineTo(w, h);
      outline.lineTo(w + dx, h + dy);
      outline.lineTo(w + dx, dy);
      outline.lineTo(dx, dy);
      outline.closePath();

      // Internal edges
      outline.moveTo(w, 0);
      outline.lineTo(0, 0);
      outline.moveTo(w, 0);
      outline.lineTo(w, h);
      outline.moveTo(w, 0);
      outline.lineTo(w + dx, dy);

      paths = { frontFace: null, topFace, rightFace, outline };
      this.pathCache.set(key, paths);
    }

    return paths;
  }

  /**
   * Clear all caches
   */
  clear(): void {
    this.pathCache.clear();
    this.offscreenCache.clear();
    this.stats = { hits: 0, misses: 0 };
  }

  /**
   * Run maintenance (LRU cleanup)
   */
  runMaintenance(): void {
    if (this.pathCache.size > RENDER_CONSTANTS.PATH_CACHE_MAX_SIZE) {
      const keys = Array.from(this.pathCache.keys()).slice(
        0,
        Math.floor(RENDER_CONSTANTS.PATH_CACHE_MAX_SIZE / 2)
      );
      keys.forEach((k) => this.pathCache.delete(k));
    }

    if (this.offscreenCache.size > RENDER_CONSTANTS.OFFSCREEN_CACHE_MAX_SIZE) {
      const keys = Array.from(this.offscreenCache.keys()).slice(
        0,
        Math.floor(RENDER_CONSTANTS.OFFSCREEN_CACHE_MAX_SIZE / 2)
      );
      keys.forEach((k) => this.offscreenCache.delete(k));
    }

    this.stats = { hits: 0, misses: 0 };
  }

  /**
   * Get cache hit rate
   */
  getHitRate(): number {
    const total = this.stats.hits + this.stats.misses;
    return total > 0 ? this.stats.hits / total : 0;
  }
}
